import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactHlsPlayer from '../ReactHlsPlayer/ReactHlsPlayer';
import css from './MediaLightbox.module.css';

const MediaLightbox = ({ media, currentIndex, onClose, onNext, onPrev }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, distance: 0 });
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const pauseOverlayTimerRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const currentMedia = media[currentIndex];
  const totalMedia = media.length;

  // Reset zoom when media changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsVideoPlaying(false);
    setShowPauseOverlay(false);
    if (pauseOverlayTimerRef.current) clearTimeout(pauseOverlayTimerRef.current);
  }, [currentIndex]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (pauseOverlayTimerRef.current) clearTimeout(pauseOverlayTimerRef.current);
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onPrev();
      if (e.key === 'ArrowRight' && currentIndex < totalMedia - 1) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, currentIndex, totalMedia]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const isImage = currentMedia?.content_type?.startsWith('image/') || 
                  /\.(png|jpe?g|webp|bmp|svg)$/i.test(currentMedia?.url || '');
  
  const urlOrName = currentMedia?.url || currentMedia?.filename || '';
  const isVideo = currentMedia?.content_type?.startsWith('video/') ||
                  /\.(mp4|mov|webm|m4v)$/i.test(urlOrName) ||
                  !!currentMedia?.video_variants?.hls;

  // Touch handlers for pinch-to-zoom
  const handleTouchStart = useCallback((e) => {
    if (!isImage) return;
    
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setTouchStart({
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
        distance
      });
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  }, [isImage, scale, position]);

  const handleTouchMove = useCallback((e) => {
    if (!isImage) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const newScale = Math.min(Math.max(1, scale * (distance / touchStart.distance)), 4);
      setScale(newScale);
      
      setTouchStart({
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
        distance
      });
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  }, [isImage, scale, touchStart, isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    if (scale <= 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  // Mouse handlers for desktop zoom
  const handleWheel = useCallback((e) => {
    if (!isImage) return;
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(1, scale * delta), 4);
    setScale(newScale);
    
    if (newScale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isImage, scale]);

  const handleMouseDown = useCallback((e) => {
    if (!isImage || scale <= 1) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [isImage, scale, position]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !isImage) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, isImage, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleBackgroundClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handlePlayButtonClick = useCallback(() => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
        setShowPauseOverlay(false);
      } else {
        videoRef.current.play();
        setIsVideoPlaying(true);
        setShowPauseOverlay(false);
      }
    }
  }, [isVideoPlaying]);

  // When video is playing and user taps, show pause overlay briefly then auto-hide
  const handleVideoAreaTap = useCallback(() => {
    if (!isVideoPlaying) {
      // Not playing — just play it
      if (videoRef.current) {
        videoRef.current.play();
        setIsVideoPlaying(true);
      }
      return;
    }
    // Playing — show pause overlay; if already visible, pause immediately
    if (showPauseOverlay) {
      if (videoRef.current) videoRef.current.pause();
      setIsVideoPlaying(false);
      setShowPauseOverlay(false);
      if (pauseOverlayTimerRef.current) clearTimeout(pauseOverlayTimerRef.current);
    } else {
      setShowPauseOverlay(true);
      if (pauseOverlayTimerRef.current) clearTimeout(pauseOverlayTimerRef.current);
      pauseOverlayTimerRef.current = setTimeout(() => {
        setShowPauseOverlay(false);
      }, 2500);
    }
  }, [isVideoPlaying, showPauseOverlay]);

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false);
    setShowPauseOverlay(false);
  }, []);

  const renderMedia = () => {
    if (isImage) {
      const imageUrl = currentMedia?.image_variants?.large || 
                      currentMedia?.image_variants?.small || 
                      currentMedia?.url;
      
      return (
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Lightbox content"
          className={css.media}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            animationDelay: '0.38s',
          }}
          onMouseDown={handleMouseDown}
          draggable={false}
        />
      );
    }

    if (isVideo) {
      // Shared overlay: play button when paused, pause button when playing (on tap)
      const playPauseOverlay = !isVideoPlaying ? (
        // Play button — always visible when paused
        <div className={css.playButtonOverlay} onClick={(e) => { e.stopPropagation(); handleVideoAreaTap(); }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="102" height="102" viewBox="0 0 102 102" fill="none">
            <path d="M51 8.5C42.5943 8.5 34.3774 10.9926 27.3883 15.6625C20.3992 20.3325 14.9519 26.9701 11.7351 34.736C8.51841 42.5018 7.67677 51.0471 9.31664 59.2913C10.9565 67.5355 15.0042 75.1083 20.948 81.052C26.8917 86.9958 34.4645 91.0435 42.7087 92.6834C50.9529 94.3232 59.4982 93.4816 67.2641 90.2649C75.0299 87.0482 81.6675 81.6008 86.3375 74.6117C91.0074 67.6226 93.5 59.4057 93.5 51C93.5 45.4188 92.4007 39.8923 90.2649 34.736C88.1291 29.5796 84.9985 24.8945 81.0521 20.948C77.1056 17.0015 72.4204 13.8709 67.2641 11.7351C62.1077 9.5993 56.5812 8.5 51 8.5ZM42.5 70.125V31.875L68 51L42.5 70.125Z" fill="white"/>
          </svg>
        </div>
      ) : showPauseOverlay ? (
        // Pause button — shown briefly when user taps while playing
        <div
          className={`${css.playButtonOverlay} ${css.pauseButtonOverlay}`}
          onClick={(e) => { e.stopPropagation(); handleVideoAreaTap(); }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="102" height="102" viewBox="0 0 102 102" fill="none">
            <circle cx="51" cy="51" r="42.5" fill="white" fillOpacity="0.9"/>
            <rect x="33" y="30" width="13" height="42" rx="3" fill="#403D45"/>
            <rect x="56" y="30" width="13" height="42" rx="3" fill="#403D45"/>
          </svg>
        </div>
      ) : null;

      return (
        <div
          className={css.videoWrapper}
          key={`video-${currentIndex}`}
          onClick={handleVideoAreaTap}
        >
          {currentMedia?.video_variants?.hls ? (
            <>
              <ReactHlsPlayer
                playerRef={videoRef}
                src={currentMedia.video_variants.hls}
                autoPlay={false}
                playsInline={true}
                controls={true}
                width="100%"
                height="100%"
                muted={false}
                poster={currentMedia?.image_variants?.thumbnail}
                className={css.video}
                style={{ width: '100%', height: 'auto', maxHeight: '100%' }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
              />
              {playPauseOverlay}
            </>
          ) : currentMedia?.video_variants?.mp4 ? (
            <>
              <video
                ref={videoRef}
                src={currentMedia.video_variants.mp4}
                controls
                playsInline
                width="100%"
                height="auto"
                poster={currentMedia?.image_variants?.thumbnail}
                className={css.video}
                style={{ width: '100%', height: 'auto' }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
              />
              {playPauseOverlay}
            </>
          ) : currentMedia?.url ? (
            <>
              <video
                ref={videoRef}
                src={currentMedia.url}
                controls
                playsInline
                width="100%"
                height="auto"
                poster={currentMedia?.image_variants?.thumbnail}
                className={css.video}
                style={{ width: '100%', height: 'auto' }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
              />
              {playPauseOverlay}
            </>
          ) : null}
        </div>
      );
    }

    return null;
  };

  return (
    <div 
      className={css.lightboxOverlay}
      onClick={handleBackgroundClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={handleWheel}
      ref={containerRef}
    >
      {/* Mobile Go Back bar — only visible on mobile, replaces close button */}
      <button className={css.mobileGoBack} onClick={onClose} aria-label="Go back">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="18"
          viewBox="0 0 11 18"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.792485 8.3323C0.402505 8.70251 0.402505 9.29749 0.792485 9.6677L8.79209 17.223C9.18407 17.5923 9.81603 17.5923 10.206 17.223C10.598 16.8538 10.598 16.256 10.206 15.8876L2.91438 9L10.206 2.11236C10.598 1.74309 10.598 1.14527 10.206 0.776951C9.81603 0.407683 9.18407 0.407683 8.79209 0.776951L0.792485 8.3323Z"
            fill="white"
            stroke="white"
          />
        </svg>
        <span>Go Back</span>
      </button>

      {/* Desktop close button with counter — hidden on mobile */}
      <div className={css.closeArea}>
        <div className={css.counter}>
          {currentIndex + 1}/{totalMedia}
        </div>
        <button className={css.closeButton} onClick={onClose} aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
  <path d="M10.9583 13.2917L2.79167 21.4583C2.48611 21.7639 2.09722 21.9167 1.625 21.9167C1.15278 21.9167 0.763888 21.7639 0.458333 21.4583C0.152777 21.1528 0 20.7639 0 20.2917C0 19.8194 0.152777 19.4306 0.458333 19.125L8.625 10.9583L0.458333 2.79167C0.152777 2.48611 0 2.09722 0 1.625C0 1.15278 0.152777 0.763888 0.458333 0.458333C0.763888 0.152777 1.15278 0 1.625 0C2.09722 0 2.48611 0.152777 2.79167 0.458333L10.9583 8.625L19.125 0.458333C19.4306 0.152777 19.8194 0 20.2917 0C20.7639 0 21.1528 0.152777 21.4583 0.458333C21.7639 0.763888 21.9167 1.15278 21.9167 1.625C21.9167 2.09722 21.7639 2.48611 21.4583 2.79167L13.2917 10.9583L21.4583 19.125C21.7639 19.4306 21.9167 19.8194 21.9167 20.2917C21.9167 20.7639 21.7639 21.1528 21.4583 21.4583C21.1528 21.7639 20.7639 21.9167 20.2917 21.9167C19.8194 21.9167 19.4306 21.7639 19.125 21.4583L10.9583 13.2917Z" fill="white"/>
</svg>
        </button>
      </div>

      {/* Previous button */}
      {currentIndex > 0 && (
        <button 
          className={`${css.navButton} ${css.prevButton}`} 
          onClick={onPrev}
          aria-label="Previous"
        >
          <svg width="83" height="84" viewBox="0 0 83 84" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_13637_12786)">
              <circle cx="41.4999" cy="37.5762" r="37.5" transform="rotate(-179.883 41.4999 37.5762)" fill="white"/>
            </g>
            <path d="M48.868 49.1087L46.653 51.3567L34.6384 39.0561C34.4448 38.859 34.2912 38.6248 34.1867 38.3669C34.0821 38.1091 34.0285 37.8326 34.0291 37.5536C34.0297 37.2745 34.0843 36.9983 34.19 36.7409C34.2956 36.4834 34.4501 36.2498 34.6445 36.0535L46.7091 23.7955L48.9128 26.0525L37.5894 37.5576L48.868 49.1087Z" fill="#403D45"/>
            <defs>
              <filter id="filter0_d_13637_12786" x="0" y="0.0761842" width="83" height="83" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="4"/>
                <feGaussianBlur stdDeviation="2"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_13637_12786"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13637_12786" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
      )}

      {/* Media content */}
      <div className={css.mediaContainer}>
        {renderMedia()}
      </div>

      {/* Next button */}
      {currentIndex < totalMedia - 1 && (
        <button 
          className={`${css.navButton} ${css.nextButton}`} 
          onClick={onNext}
          aria-label="Next"
        >
          <svg width="83" height="83" viewBox="0 0 83 83" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_13637_12785)">
              <circle cx="41.5" cy="37.5" r="37.5" fill="white"/>
            </g>
            <path d="M34.1083 25.9825L36.3187 23.73L48.3583 36.0061C48.5524 36.2028 48.7064 36.4368 48.8115 36.6944C48.9166 36.9521 48.9707 37.2284 48.9707 37.5074C48.9707 37.7865 48.9166 38.0628 48.8115 38.3205C48.7064 38.5781 48.5524 38.8121 48.3583 39.0088L36.3187 51.2913L34.1104 49.0388L45.4104 37.5106L34.1083 25.9825Z" fill="#403D45"/>
            <defs>
              <filter id="filter0_d_13637_12785" x="0" y="0" width="83" height="83" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="4"/>
                <feGaussianBlur stdDeviation="2"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_13637_12785"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13637_12785" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>
      )}

      {/* Dots indicator */}
      {totalMedia > 1 && (
        <div className={css.dotsContainer}>
          {media.map((_, index) => (
            <span
              key={index}
              className={`${css.dot} ${index === currentIndex ? css.activeDot : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLightbox;
